import { useEffect, useMemo, useState } from 'react'
import type { SelectionTarget } from '@viewer/store/selectionSlice'
import { viewerApi } from '@viewer/lib/viewerApi'
import Section from './Section'
import { computeCompare, MAX_COMPARE_COLUMNS, type CompareRow } from './compareElements'
import type { KeyValue } from '@viewer/lib/ifcItemData'
import {
  idDoType,
  juntarComType,
  lerConjuntosDosTypes,
  lerElementos,
  type ConjuntosDoElemento,
} from '@viewer/lib/psetsHerdados'
import { getPsetsAndQsets } from '@viewer/lib/ifcItemData'
import { useNomesDosModelos } from '@viewer/lib/useNomeDoModelo'
import { obterUnidadesDoProjecto, unidadePropria } from '@viewer/lib/unidadesDoModelo'
import { rotuloDaUnidade, unidadeDaPropriedade } from '@viewer/lib/unidades'

// Vista de selecção múltipla, tirada do PropertiesPanel para o
// ficheiro deixar de ter mais de 2000 linhas. A comparação (pura)
// continua em compareElements.ts; aqui só a renderização.

// Matriz de comparação: coluna do nome + uma por elemento. Realça as
// linhas que variam; "-" quando o elemento não tem a chave.
function CompareTable({
  rows,
  labels,
  modelos = [],
  completos = [],
}: {
  rows: CompareRow[]
  labels: string[]
  /** O nome curto do modelo de cada coluna (só com vários modelos). */
  modelos?: (string | null)[]
  /** O nome completo do ficheiro de cada coluna, para o tooltip. */
  completos?: (string | null)[]
}) {
  if (rows.length === 0) {
    return (
      <div className="px-1 py-2 text-[11px] italic text-[var(--color-text-muted)]">
        Sem campos nestes elementos.
      </div>
    )
  }
  return (
    <div className="overflow-x-auto">
      <table className="border-collapse text-[11px]">
        <thead>
          <tr className="border-b border-[var(--color-border)]/60">
            <th className="sticky left-0 z-10 bg-[var(--color-surface)] py-1 pr-2 text-left font-medium text-[var(--color-text-muted)]">
              propriedade
            </th>
            {labels.map((l, i) => (
              <th
                key={`${l}-${i}`}
                title={completos[i] ? `${l} · ${completos[i]}` : l}
                className="max-w-[120px] truncate px-2 py-1 text-left font-medium text-[var(--color-text)]"
              >
                {l}
                {modelos[i] && (
                  <div className="truncate font-mono text-[9px] font-normal text-[var(--color-text-muted)]">
                    {modelos[i]}
                  </div>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={`${r.key}-${i}`}
              className={
                'border-b border-[var(--color-border)]/40 last:border-b-0 ' +
                (r.allEqual ? '' : 'bg-[var(--color-accent-bg)]')
              }
            >
              <td
                className="sticky left-0 z-10 whitespace-nowrap bg-[var(--color-surface)] py-1 pr-2 align-top text-[var(--color-text-muted)]"
                title={r.ifcType}
              >
                {r.key}
              </td>
              {r.values.map((v, j) => (
                <td
                  key={j}
                  className={
                    'max-w-[160px] break-words px-2 py-1 align-top ' +
                    (r.allEqual
                      ? 'text-[var(--color-text)]'
                      : 'text-[var(--color-warn-text)]')
                  }
                >
                  {v ?? (
                    <span className="text-[var(--color-text-dim)]">-</span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function MultiSelectionView({ targets }: { targets: SelectionTarget[] }) {
  // A carga guarda consigo a chave da selecção de que veio, e fica
  // velha sozinha quando a selecção muda. Antes era limpa no corpo do
  // efeito, o que encadeia renders e deixava um render a mostrar as
  // propriedades da selecção anterior.
  const [carga, setCarga] = useState<{
    chave: string
    items: Record<string, unknown>[] | null
    /** Por elemento: os conjuntos com os do type e a unidade de cada
     *  linha (do modelo de onde o elemento vem). */
    conjuntos: ConjuntosDoElemento[]
    unidades: ((linha: KeyValue) => string | null)[]
    /** O modelo de origem de cada elemento (alinhado com items). */
    modelIds: string[]
    error: string | null
  }>({ chave: '', items: null, conjuntos: [], unidades: [], modelIds: [], error: null })

  // Debounce-ish key: stringify targets so React only re-fetches when
  // the atual selection set changes (not on unrelated re-renders).
  const targetsKey = useMemo(
    () =>
      targets
        .map((t) => `${t.modelId}:${t.localId}`)
        .sort()
        .join('|'),
    [targets],
  )

  const actual = carga.chave === targetsKey
  const items = actual ? carga.items : null
  const conjuntos = actual ? carga.conjuntos : null
  const unidades = actual ? carga.unidades : null
  const modelIds = actual ? carga.modelIds : null
  const { nomeDe, varios } = useNomesDosModelos()
  // O modelo de cada coluna, só com vários modelos abertos (ver
  // useNomesDosModelos); o nome completo vai sempre no tooltip.
  const completos = (modelIds ?? []).map((id) => nomeDe(id)?.completo ?? id)
  const error = actual ? carga.error : null
  const loading = targets.length > 0 && !actual

  useEffect(() => {
    if (targets.length === 0) return
    let cancelled = false

    // Group local IDs by model so we issue one getItemsData call per
    // model. Só os primeiros MAX_COMPARE_COLUMNS elementos entram na
    // matriz (acima disso fica ilegível). O pedido aprofunda só o
    // IsDefinedBy, como o painel: com relations:true em tudo vinha o
    // piso, os elementos do piso e as relações deles (7785 entidades
    // por duas paredes num modelo de 25 MB). Os Psets do type lêem-se
    // à parte e juntam-se, marcados.
    const byModel = new Map<string, number[]>()
    for (const t of targets.slice(0, MAX_COMPARE_COLUMNS)) {
      const arr = byModel.get(t.modelId)
      if (arr) arr.push(t.localId)
      else byModel.set(t.modelId, [t.localId])
    }

    type Lido = {
      modelId: string
      item: Record<string, unknown>
      conjuntos: ConjuntosDoElemento
      unidade: (linha: KeyValue) => string | null
    }
    Promise.all(
      Array.from(byModel.entries()).map(async ([modelId, localIds]): Promise<Lido[]> => {
        const model = viewerApi.getModel(modelId)
        if (!model) return []
        const data = await lerElementos(model, localIds)
        const lidos = (data as unknown[]).filter((d): d is Record<string, unknown> => !!d && typeof d === 'object')
        const tipos = await lerConjuntosDosTypes(
          model,
          lidos.map(idDoType).filter((id): id is number => id !== null),
        )
        const projecto = await obterUnidadesDoProjecto(modelId)
        const unidade = (linha: KeyValue): string | null =>
          projecto
            ? rotuloDaUnidade(
                unidadeDaPropriedade(linha.medida, unidadePropria(modelId, linha.localId), projecto.porTipo, projecto.ambiguas),
              )
            : null
        return lidos.map((item) => {
          const t = idDoType(item)
          return {
            modelId,
            item,
            conjuntos: juntarComType(getPsetsAndQsets(item), t !== null ? tipos.get(t) : undefined),
            unidade,
          }
        })
      }),
    )
      .then((arrays) => {
        if (cancelled) return
        const flat = arrays.flat()
        setCarga({
          chave: targetsKey,
          items: flat.map((l) => l.item),
          conjuntos: flat.map((l) => l.conjuntos),
          unidades: flat.map((l) => l.unidade),
          modelIds: flat.map((l) => l.modelId),
          error: null,
        })
      })
      .catch((err: unknown) => {
        if (cancelled) return
        setCarga({
          chave: targetsKey,
          items: null,
          conjuntos: [],
          unidades: [],
          modelIds: [],
          error: err instanceof Error ? err.message : String(err),
        })
      })

    return () => {
      cancelled = true
    }
    // targetsKey captures the meaningful identity of `targets`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetsKey])

  const compare = useMemo(() => {
    if (!items || items.length === 0) return null
    return computeCompare(items, {
      conjuntos: conjuntos ?? undefined,
      unidade: (i, linha) => unidades?.[i]?.(linha) ?? null,
      modelos: varios ? (modelIds ?? []).map((id) => nomeDe(id)?.minimo ?? null) : undefined,
    })
  }, [items, conjuntos, unidades, modelIds, varios, nomeDe])

  if (loading && !compare) {
    return (
      <div className="p-4 text-xs text-[var(--color-text-muted)]">
        A carregar dados de {targets.length} elementos…
      </div>
    )
  }
  if (error) {
    return (
      <div className="m-3 rounded-md border border-[var(--color-error-border)] bg-[var(--color-error-bg)] p-2 text-xs text-[var(--color-error-text)]">
        {error}
      </div>
    )
  }
  if (!compare) return null

  const truncated = targets.length > MAX_COMPARE_COLUMNS

  return (
    <div>
      {compare.commonCategory && (
        <div className="border-b border-[var(--color-border)] bg-[var(--color-bg)]/40 px-3 py-2 text-[11px] text-[var(--color-text)]">
          Todos os elementos selecionados são{' '}
          <span className="font-mono text-[var(--color-accent)]">
            {compare.commonCategory}
          </span>
          .
        </div>
      )}
      {truncated && (
        <div className="border-b border-[var(--color-border)] bg-[var(--color-accent-bg)] px-3 py-2 text-[10px] text-[var(--color-warn-text)]">
          {targets.length} elementos selecionados; a comparar os primeiros{' '}
          {MAX_COMPARE_COLUMNS}.
        </div>
      )}

      <Section
        title="Atributos"
        count={compare.attributes.length}
        defaultOpen
      >
        <CompareTable rows={compare.attributes} labels={compare.labels} modelos={compare.modelos} completos={completos} />
      </Section>

      <Section
        title="Property sets"
        count={compare.psets.length}
        defaultOpen={false}
      >
        {compare.psets.length === 0 ? (
          <div className="px-1 py-2 text-[11px] italic text-[var(--color-text-muted)]">
            Sem property sets nestes elementos.
          </div>
        ) : (
          compare.psets.map((g, i) => (
            <div
              key={`${g.name}-${i}`}
              className="mb-3 last:mb-0 rounded border border-[var(--color-border)]/60 bg-[var(--color-bg)]/40"
            >
              <div className="border-b border-[var(--color-border)]/60 px-2 py-1 text-[11px] font-medium text-[var(--color-text)]">
                {g.name}
                <span className="ml-2 text-[10px] text-[var(--color-text-muted)]">
                  {g.ifcClass}
                </span>
              </div>
              <div className="px-2 pb-1">
                <CompareTable rows={g.rows} labels={compare.labels} modelos={compare.modelos} completos={completos} />
              </div>
            </div>
          ))
        )}
      </Section>

      <Section
        title="Quantity sets"
        count={compare.qsets.length}
        defaultOpen={false}
      >
        {compare.qsets.length === 0 ? (
          <div className="px-1 py-2 text-[11px] italic text-[var(--color-text-muted)]">
            Sem quantity sets nestes elementos.
          </div>
        ) : (
          compare.qsets.map((g, i) => (
            <div
              key={`${g.name}-${i}`}
              className="mb-3 last:mb-0 rounded border border-[var(--color-border)]/60 bg-[var(--color-bg)]/40"
            >
              <div className="border-b border-[var(--color-border)]/60 px-2 py-1 text-[11px] font-medium text-[var(--color-text)]">
                {g.name}
                <span className="ml-2 text-[10px] text-[var(--color-text-muted)]">
                  {g.ifcClass}
                </span>
              </div>
              <div className="px-2 pb-1">
                <CompareTable rows={g.rows} labels={compare.labels} modelos={compare.modelos} completos={completos} />
              </div>
            </div>
          ))
        )}
      </Section>
    </div>
  )
}
